import type { TemplateContextValue, TemplateListEntry } from "@compass/shared/schema/api/templates";
import type { TipTapDoc } from "@compass/shared/schema/tiptapDoc";
import {
    resolveTemplatePlaceholders,
    resolveTemplateSubject,
} from "@compass/shared/tiptap/resolveTemplatePlaceholders";
import * as Dialog from "@radix-ui/react-dialog";
import CloseIcon from "assets/img/icons/header/close.svg?react";
import { useCurrentUser } from "hooks/useCurrentUser";
import { trpc } from "lib/trpc";

export type { TemplateListEntry };

/** Runtime values used to fill placeholder tokens at selection time. */
export type TemplateResolveValues = {
    participantNames?: string[];
    replyToName?: string;
    organizationName?: string;
    userFirstName?: string;
    userLastName?: string;
};

/** Resolve a template's subject + body against the given runtime values. */
export function resolveTemplate(
    template: TemplateListEntry,
    values: TemplateResolveValues,
): { subject: string; body: TipTapDoc } {
    return {
        subject: template.subject ? resolveTemplateSubject(template.subject, values) : "",
        body: resolveTemplatePlaceholders(template.contentJson, values),
    };
}

/** Templates for a context, gated to Compass users. `enabled` defers the fetch. */
export function useTemplateList(kind: TemplateContextValue, enabled: boolean) {
    const currentUser = useCurrentUser();
    const isCompassUser = Boolean(currentUser?.isCompassUser);
    const query = trpc.templates.list.useQuery(
        { context: kind },
        { enabled: enabled && isCompassUser },
    );
    return {
        templates: query.data ?? [],
        isLoading: query.isLoading,
        isCompassUser,
    };
}

export function TemplateList({
    templates,
    isLoading,
    onSelect,
}: {
    templates: TemplateListEntry[];
    isLoading: boolean;
    onSelect: (template: TemplateListEntry) => void;
}) {
    if (isLoading) {
        return <div className="px-1 py-4 text-sm text-gray-500">Loading templates…</div>;
    }
    if (templates.length === 0) {
        return <div className="px-1 py-4 text-sm text-gray-500">No templates available.</div>;
    }
    return (
        <ul className="flex flex-col gap-1">
            {templates.map((template) => (
                <li key={template.id}>
                    <button
                        type="button"
                        onClick={() => onSelect(template)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-left hover:border-blue-400 hover:bg-blue-50"
                    >
                        <div className="text-sm font-medium text-[#424242]">{template.name}</div>
                        {template.subject && (
                            <div className="mt-0.5 truncate text-xs text-gray-500">
                                Subject: {template.subject}
                            </div>
                        )}
                    </button>
                </li>
            ))}
        </ul>
    );
}

export function TemplatePickerDialog({
    open,
    onOpenChange,
    kind,
    onSelect,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    kind: TemplateContextValue;
    onSelect: (template: TemplateListEntry) => void;
}) {
    const { templates, isLoading } = useTemplateList(kind, open);

    return (
        <Dialog.Root open={open} onOpenChange={onOpenChange}>
            <Dialog.Overlay className="fixed inset-0 z-60 bg-[#424242]/75" />
            <Dialog.Content className="fixed top-1/2 left-1/2 z-60 max-h-[85vh] w-[95vw] max-w-[480px] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-2xl bg-white p-4 shadow-lg sm:p-6">
                <div className="flex items-center justify-between">
                    <div className="text-[20px] font-bold text-[#424242]">
                        Start with a template
                    </div>
                    <Dialog.Close>
                        <CloseIcon className="h-[26px] w-[26px]" />
                    </Dialog.Close>
                </div>
                <hr className="-mx-4 my-3 border-[#c8c8c8] sm:-mx-6" />
                <TemplateList
                    templates={templates}
                    isLoading={isLoading}
                    onSelect={(template) => {
                        onSelect(template);
                        onOpenChange(false);
                    }}
                />
            </Dialog.Content>
        </Dialog.Root>
    );
}
